/**
 * TableOptions state (spec §2.4, DESIGN-client §7): every toggle bound to a shared `RulesInput`
 * with House Rules defaults, plus the Blitz timer preset and the match format.
 */
import { BLITZ_TIMERS, HOUSE_RULES, type Rules, type RulesInput, resolveRules } from '@spades/shared';

export type FormatChoice = 'quick' | 'classic500' | 'classic300';

export interface OptionsState {
  jokers: boolean;
  deucesHigh: boolean;
  nil: boolean;
  blindNil: boolean;
  board: boolean;
  tenFor200: boolean;
  bagsPenalty: boolean;
  breakSpades: boolean;
  sequentialBidding: boolean;
  blitz: boolean;
  format: FormatChoice;
}

export interface OptionDef {
  key: keyof OptionsState;
  label: string;
  hint: string;
}

/** Display order + copy for the toggles. */
export const OPTION_DEFS: readonly OptionDef[] = [
  { key: 'jokers', label: 'Jokers', hint: 'Off: classic 52-card deck, ace of spades high' },
  { key: 'deucesHigh', label: 'Deuces high', hint: '2♦ and 2♠ sit above the ace of spades' },
  { key: 'nil', label: 'Nil', hint: 'Bid zero for ±100' },
  { key: 'blindNil', label: 'Blind nil', hint: 'Only when trailing by 100+, ±200' },
  { key: 'board', label: 'Board', hint: 'Team contracts under 4 round up to 4' },
  { key: 'tenFor200', label: '10 for 200', hint: 'Team bid of 10 or more scores 200' },
  { key: 'bagsPenalty', label: 'Bags penalty', hint: '10 bags cost 100 and the counter resets' },
  { key: 'breakSpades', label: 'Break spades', hint: 'No leading spades until one is cut' },
  { key: 'sequentialBidding', label: 'Sequential bidding', hint: 'Classic turn order instead of a secret simultaneous bid' },
  { key: 'blitz', label: 'Blitz timers', hint: '20s bid, 10s play' },
];

export function isBlitz(rules: Pick<Rules, 'timers'>): boolean {
  return rules.timers.bidMs === BLITZ_TIMERS.bidMs && rules.timers.playMs === BLITZ_TIMERS.playMs;
}

export function formatOf(rules: Pick<Rules, 'format'>): FormatChoice {
  const f = rules.format;
  if (f.kind === 'classic') return f.target === 300 ? 'classic300' : 'classic500';
  return 'quick';
}

/** State from a full `Rules` object (House Rules by default). */
export function fromRules(rules: Rules = HOUSE_RULES): OptionsState {
  return {
    jokers: rules.jokers,
    deucesHigh: rules.deucesHigh,
    nil: rules.nil,
    blindNil: rules.blindNil,
    board: rules.board,
    tenFor200: rules.tenFor200,
    bagsPenalty: rules.bagsPenalty,
    breakSpades: rules.breakSpades,
    sequentialBidding: rules.bidding === 'sequential',
    blitz: isBlitz(rules),
    format: formatOf(rules),
  };
}

export const defaultOptions = (): OptionsState => fromRules(HOUSE_RULES);

/** What the client sends (`setRules` / `POST /api/rooms`): only fields that differ are needed, but explicit is clearer. */
export function toRulesInput(s: OptionsState): RulesInput {
  const format: RulesInput['format'] =
    s.format === 'quick' ? { kind: 'quick', hands: 4 } : { kind: 'classic', target: s.format === 'classic300' ? 300 : 500 };
  return {
    jokers: s.jokers,
    deucesHigh: s.deucesHigh,
    nil: s.nil,
    blindNil: s.blindNil,
    board: s.board,
    tenFor200: s.tenFor200,
    bagsPenalty: s.bagsPenalty,
    breakSpades: s.breakSpades,
    bidding: s.sequentialBidding ? 'sequential' : 'simultaneous',
    format,
    timers: s.blitz ? { ...BLITZ_TIMERS } : {},
  };
}

/** Resolved rules the server would produce from this state. */
export const toRules = (s: OptionsState): Rules => resolveRules(toRulesInput(s));

/** Parse `TableSchema.rulesJson` (empty/invalid → House Rules). */
export function rulesFromJson(json: string): Rules {
  if (!json) return HOUSE_RULES;
  try {
    return resolveRules(JSON.parse(json) as RulesInput);
  } catch {
    return HOUSE_RULES;
  }
}

/** Short human summary for the lobby ("House Rules", or the deltas). */
export function summarize(s: OptionsState): string {
  const d = defaultOptions();
  const parts: string[] = [];
  if (s.format !== d.format) parts.push(s.format === 'quick' ? '4 hands' : s.format === 'classic500' ? 'to 500' : 'to 300');
  if (s.blitz) parts.push('Blitz');
  for (const def of OPTION_DEFS) {
    if (def.key === 'blitz' || def.key === 'format') continue;
    if (s[def.key] !== d[def.key]) parts.push(`${def.label} ${s[def.key] ? 'on' : 'off'}`);
  }
  return parts.length === 0 ? 'House Rules' : parts.join(' · ');
}
